import { PageHero, LocalBusinessSchema } from '@components/common';
import { CALENDLY_URL } from '@constants';
import { Shield, Target, Users, Clock, MapPin } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function BrightonPage() {
  return (
    <>
      <LocalBusinessSchema city="Brighton" latitude={50.8225} longitude={-0.1372} />
      <PageHero
        title="Fractional CISO Services in Brighton"
        subtitle="Cybersecurity leadership for the South Coast's digital creative hub"
        eyebrow="Brighton"
        seoTitle="Fractional CISO Brighton | The CISO Network"
        seoDescription="Expert fractional CISO services for Brighton businesses. Access senior cybersecurity leadership for digital agencies and creative tech."
      />

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Securing the South Coast's Creative Digital Economy
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Brighton has established itself as one of the UK's most vibrant digital and
                  creative hubs, with a unique ecosystem spanning digital agencies, gaming, media
                  technology, and healthtech. The city's reputation as "Silicon Beach" reflects its
                  status as a magnet for creative talent and innovative businesses seeking a coastal
                  alternative to London.
                </p>
                <p>
                  Brighton businesses face distinctive cybersecurity challenges. Digital agencies
                  handle high-profile client data and valuable creative assets that require robust
                  protection. Gaming and media tech companies face threats against their
                  intellectual property and user data. Meanwhile, the city's growing healthtech
                  sector must navigate complex NHS and data protection requirements.
                </p>
                <p>
                  Many Brighton businesses are creative agencies or tech startups serving UK and
                  international clients. These companies need credible security leadership to win
                  enterprise contracts and build client trust - but often find London CISOs too
                  remote or expensive, while local talent is scarce. Our fractional CISO service
                  provides Brighton businesses with accessible, creative-sector-aware security
                  expertise.
                </p>
                <p>
                  Brighton has developed one of the UK's most dynamic digital economies, with a
                  concentration of SaaS startups, creative agencies, and remote-first technology
                  companies. These businesses typically handle significant customer data but lack
                  the in-house security expertise of larger corporate peers. A fractional CISO
                  provides the security leadership that helps Brighton firms compete for enterprise
                  clients and investment.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Your Account Manager
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Your account manager understands the unique culture of Silicon Beach and its
                      creative digital economy.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Clock className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Sussex Coverage
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Supporting businesses across Brighton, Hove, and the wider Sussex digital
                      corridor.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Why Brighton Businesses Choose Fractional CISOs
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Creative-sector security leadership for Silicon Beach
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Creative IP Protection',
                description:
                  'Secure valuable creative assets, client data, and digital productions.',
              },
              {
                icon: Target,
                title: 'Client Trust',
                description:
                  'Demonstrable security leadership that wins enterprise client contracts.',
              },
              {
                icon: Users,
                title: 'Agency Experience',
                description:
                  'CISOs who understand the fast-paced world of digital agencies and creative tech.',
              },
              {
                icon: Clock,
                title: 'Flexible Engagement',
                description: "Security leadership that scales with your agency's project pipeline.",
              },
            ].map((benefit, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <benefit.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO Services in Brighton - FAQs"
        subtitle="Common questions from Brighton and Sussex businesses"
        items={[
          {
            id: 'brighton-1',
            question: 'How does a fractional CISO work with businesses in Brighton?',
            answer:
              'Our fractional CISOs work with organisations across the UK, including Brighton. They can attend meetings on-site or work remotely, depending on what suits your business. We match you with a CISO who understands the security challenges facing digital, creative, and SaaS companies.',
          },
          {
            id: 'brighton-2',
            question: 'What size of business benefits from a fractional CISO in Brighton?',
            answer:
              'Fractional CISO services are ideal for growing businesses that need strategic security leadership but are not yet ready for a full-time executive hire. This includes Brighton startups, scale-ups, and mid-sized companies that are winning larger contracts or facing regulatory requirements.',
          },
          {
            id: 'brighton-3',
            question: 'What industries do your CISOs cover in Brighton?',
            answer:
              "Our network includes CISOs with experience in digital, creative, and SaaS. We match you with someone who understands your sector's specific regulatory environment and threat landscape, whether you are navigating FCA requirements, NHS Digital standards, or customer security questionnaires.",
          },
          {
            id: 'brighton-4',
            question: 'What is the first thing a fractional CISO does when they start?',
            answer:
              'Every engagement begins with discovery. Your CISO assesses your current security posture, understands your business priorities, and develops a tailored roadmap. This typically delivers actionable insights within the first 30 days, with quick wins identified early.',
          },
          {
            id: 'brighton-5',
            question: 'Can a fractional CISO help with board reporting and governance?',
            answer:
              'Yes. A core part of the fractional CISO role is translating technical security risks into business language for boards and leadership teams. Our CISOs build reporting frameworks, present to boards, and establish governance structures that satisfy regulators and investors.',
          },
        ]}
      />

      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Secure Your Brighton Business?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Speak with our team today and discover how a fractional CISO can protect your creative
            digital business.
          </p>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
            Book a Call
          </a>
        </div>
      </section>
    </>
  );
}
